import { useFormik } from "formik";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import Select from "react-select";
import Datetime from "react-datetime";
import { Button, Card, CardBody, Col, Collapse, Form, FormGroup, Input, Label, Row, Nav, TabContent, TabPane, NavItem, NavLink, InputGroup, InputGroupText, FormFeedback, UncontrolledAlert, } from "reactstrap";
import InputField from "../../../../components/InputField";
import { CombinePDFSizeValidationMessage, featurePermissions, ImageLinks, ImageType, loyaltyProgramInputFields, reservationTimeInterval, selectDropdownStyle, settingsInputFields, settingTabs, } from "../../../../constants/General";
import { editSettingsSchema } from "../../../../constants/Schemas";
import { createStripeAccount, editSettings, updateAILinksSettings, initializeServiceHours, linkBankAccounts, setInitialValues, stripeAccountLogin, } from "../../utils/middleware/settings";
import { useEffect } from "react";
import { isAllowFeature, removeEmptyKeysFromObject, uploadImage, uploadWithPresignedUrl } from "../../../../services/middleware";
import classnames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock, faMinus, faPlus } from "@fortawesome/free-solid-svg-icons";
import { Actions } from "../../../../redux/actions";
import { handleError } from "../../../../utils/toast";
import { dispatch } from "../../../../utils/store";

export default function Settings() {
  // Store
  const settings = useSelector((state) => state.restaurantAdmin.settings);

  // State
  const [editable, setEditable] = useState(false);
  const [allowLoyalty, setAllowLoyalty] = useState(false);
  const [pushNotify, setPushNotify] = useState(false);
  const [emailNotify, setEmailNotify] = useState(false);
  const [isReservationUrl, setIsReservationUrl] = useState(false);
  const [reservationUrl, setReservationUrl] = useState("");
  const [isCallbackurlCopy, setIsCallBackurlCopy] = useState(false);
  const [isVerifyTokenCopy, setIsVerifyTokenCopy] = useState(false);
  const [aiLinks, setAiLinks] = useState([]);
  const [triggerReplies, setTriggerReplies] = useState([
    { title: "", message: "" },
  ]);
  const [serviceHours, setServiceHours] = useState(
    initializeServiceHours(settings?.serviceHours)
  );
  const [activeTab, setActiveTab] = useState(0);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: setInitialValues(settings),
    validationSchema: editSettingsSchema,
    onSubmit: async (values, { resetForm }) => {
      values.reservationTimeInterval =
        values.reservationTimeInterval?.value || "";
      const filterData = removeEmptyKeysFromObject(values);
      if (
        filterData.welcomePDF &&
        Array.isArray(filterData.welcomePDF) &&
        filterData.welcomePDF?.length
      ) {
        dispatch(Actions.RestaurantAdmin.SetLoading, true);
        let welcomePDFData = [];
        let fileData = filterData.welcomePDF.filter(
          (file) => typeof file.fileUrl === "object"
        );
        let uploadedData = filterData.welcomePDF.filter(
          (file) => typeof file.fileUrl === "string"
        );
        if (fileData?.length) {
          const logoResponse = await uploadImage(
            fileData?.length > 1 ? fileData : fileData[0],
            ImageType.RESTAURANT_MENU_PDF,
            true
          ).catch((err) => {
            dispatch(Actions.RestaurantAdmin.SetLoading, false);
            handleError(err);
          });
          const extractFileName = (filePath) =>
            filePath.split("/").pop().replace(".pdf", "");
          logoResponse.forEach((file) =>
            welcomePDFData.push({
              fileName: extractFileName(file?.fileName),
              fileUrl: file?.fileLink,
              isFileNameUpdate:
                extractFileName(file?.fileName) !==
                extractFileName(file.fileLink),
            })
          );
        }
        uploadedData.forEach((file) => {
          if (file?.fileName && file.fileUrl) {
            welcomePDFData.push({
              fileName: file?.fileName,
              fileUrl: file?.fileUrl,
              isFileNameUpdate:
                file.fileName !==
                file.fileUrl.split("/").pop().replace(".pdf", ""),
            });
          }
        });
        filterData.welcomePDF = welcomePDFData;
        filterData.isEmailNotificationEnabled = emailNotify;
        filterData.isPushNotificationEnabled = pushNotify;
        filterData.reservationThroughUrl = isReservationUrl;
        // filterData.instruction = values.instruction || "";
        // filterData.secretKey = values.secretKey || "";;
        // filterData.temperature = values.temperature || "";;
        // filterData.apiKey = values.apiKey || "";;
        // filterData.aiLinks = aiLinks;
      }
      editSettings(
        { ...filterData, allowLoyalty: true },
        resetForm,
        setEditable
      );
    },
  });

  const handleAddTriggerReply = () => {
    setTriggerReplies([...triggerReplies, { title: "", message: "" }]);
  };

  const handleRemoveTriggerReply = (index) => {
    const updated = [...triggerReplies];
    updated.splice(index, 1);
    setTriggerReplies(updated);
  };

  const updateAILinks = async () => {
    // Logic to update AI Links
    const filterData = {};
    // filterData.instruction = formik.values.instruction || "";
    // filterData.secretKey = formik.values.secretKey || "";
    filterData.temperature = formik.values.temperature || "";
    filterData.apiKey = formik.values.apiKey || "";
    filterData.aiLinks = aiLinks;
    filterData.GOOGLE_CLIENT_ID = formik.values.googleClientId || "";
    filterData.GOOGLE_CLIENT_SECRET = formik.values.googleClientSecret || "";
    filterData.GOOGLE_REFRESH_TOKEN = formik.values.googleRefreshToken || "";
    filterData.GOOGLE_LOCATION_ID = formik.values.googleLocationId || "";
    filterData.GOOGLE_ACCOUNT_ID = formik.values.googleAccountId || "";
    console.log("filterData", filterData);
    updateAILinksSettings(filterData);
  }

  const handleAddLink = (type = 'link') => {
    const newEntry = { url: '', type, id: null }; // 'upload' or 'url'
    setAiLinks([...aiLinks, newEntry]);
  };

  const handleRemoveLink = (index) => {
    if (aiLinks.length <= 1) {
      return; // Prevent removing the last link
    }
    // If the link has an id, it means it's already uploaded, so we can remove it
    const updatedList = [...aiLinks];
    updatedList.splice(index, 1);
    setAiLinks(updatedList);
  };

  const handleFileUpload = async (e, index) => {
    const file = e.target.files[0];
    const fileType = file?.type?.split('/')[0];

    if (file) {
      try {
        const response = await uploadWithPresignedUrl(file, ImageType.CAMPAIGN_PDF);

        const updatedLinks = [...aiLinks];
        updatedLinks[index] = {
          ...updatedLinks[index],
          url: response
        };
        setAiLinks(updatedLinks);
      } catch (err) {
        dispatch(Actions.RestaurantAdmin.SetLoading, false);
        handleError(err);
      }
    }
  };


  useEffect(() => {
    setServiceHours(initializeServiceHours(settings?.serviceHours));
    setAllowLoyalty(settings?.allowLoyalty);
    setPushNotify(settings?.isPushNotificationEnabled);
    setEmailNotify(settings?.isEmailNotificationEnabled)
    setIsReservationUrl(settings?.reservationThroughUrl)
    setReservationUrl(settings?.reservationUrl)
    if (settings?.aiLinks && settings?.aiLinks.length > 0) {
      setAiLinks(settings?.aiLinks)
    } else {
      const initialLink = [
        {
          url: "",
          type: "link", // Default type is 'link'
          id: null
        }
      ]
      setAiLinks(initialLink)
    }
  }, [settings]);

  const updateSetting = () => {
    return (
      <div className="text-right">
        {editable ? (
          <>
            <Button
              color="primary"
              className="mt-4"
              type="button"
              disabled={!!Object.values(formik.errors)?.length}
              onClick={formik.handleSubmit}
            >
              Update
            </Button>
            <Button
              color="outline-primary"
              className="mt-4"
              type="button"
              onClick={() => {
                formik.setValues(setInitialValues(settings));
                setAllowLoyalty(settings?.allowLoyalty);
                setServiceHours(initializeServiceHours(settings?.serviceHours));
                setEditable(false);
              }}
            >
              Cancel
            </Button>
          </>
        ) : (
          <Button
            color="primary"
            className="mt-4"
            type="button"
            onClick={() => setEditable(true)}
          >
            Edit
          </Button>
        )}
      </div>
    );
  };

  const updateAISetting = () => {
    return (
      <div className="text-right">
        <Button
          color="primary"
          className="mt-4"
          type="button"
          onClick={() => updateAILinks()}
        >
          Edit
        </Button>
      </div>
    );
  };

  const days = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  useEffect(() => {
    let data = [];
    if (serviceHours && Object.keys(serviceHours).length) {
      data = Object.keys(serviceHours).map((service) => {
        let timeSlots = [];
        if (serviceHours?.[service]?.from1 && serviceHours?.[service]?.to1) {
          timeSlots.push({
            from: moment(serviceHours?.[service]?.from1).format("HH:mm"),
            to: moment(serviceHours?.[service]?.to1).format("HH:mm"),
          });
        }
        if (serviceHours?.[service]?.from2 && serviceHours?.[service]?.to2) {
          timeSlots.push({
            from: moment(serviceHours?.[service]?.from2).format("HH:mm"),
            to: moment(serviceHours?.[service]?.to2).format("HH:mm"),
          });
        }
        return {
          day: parseInt(service),
          timeSlots: timeSlots,
        };
      });
    }
    formik.setFieldValue("serviceHours", data);
  }, [serviceHours]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCopyText = (text, copyTextVisible) => {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    document.body.appendChild(textarea);

    // Select and copy the text
    textarea.select();
    document.execCommand("copy");

    // Clean up the temporary element
    document.body.removeChild(textarea);
    copyTextVisible(true);

    setTimeout(() => {
      copyTextVisible(false);
    }, 5000); // Set timeout for 5 seconds
  };

  const handleRemove = (index) => {
    let updatedPdfs = [...formik.values.welcomePDF];
    if (updatedPdfs.length === 1) {
      updatedPdfs = [{ fileName: "", fileUrl: "" }];
    } else {
      updatedPdfs.splice(index, 1);
    }
    formik?.setFieldValue("welcomePDF", updatedPdfs);
  };

  const handleAdd = () => {
    const updatedPdfs = [
      ...formik.values.welcomePDF,
      { fileName: "", fileUrl: "" },
    ];
    formik?.setFieldValue("welcomePDF", updatedPdfs);
  };


  return (
    <div>
      {activeTab === 3 && (
        <UncontrolledAlert color="light">
          {CombinePDFSizeValidationMessage}
        </UncontrolledAlert>
      )}
      <Card className="shadow overflow-hidden">
        <CardBody className="p-0">
          <div className="custom-tabs-wrapper">
            <Nav tabs className="custom-tab-links">
              {settingTabs.map((tab, index) => {
                return (
                  (index !== 2 ||
                    (tab === featurePermissions["loyalty"] &&
                      isAllowFeature("loyalty"))) && (
                    <NavItem key={index}>
                      <NavLink
                        className={classnames({
                          active: activeTab === index,
                        })}
                        onClick={() => {
                          setActiveTab(index);
                        }}
                      >
                        <h3>{tab}</h3>
                      </NavLink>
                    </NavItem>
                  )
                );
              })}
            </Nav>
            <TabContent activeTab={activeTab} className="custom-tab-content">
              <TabPane tabId={0}>
                <div className="tab-content-inner-wrap">
                  <Form>
                    {settingsInputFields.map((row, index) => {
                      return (
                        <Row key={index}>
                          <Col md={6}>
                            <div className="flex align-items-end">
                              <div className="full-width">
                                {row.fieldNameLeft && (
                                  <InputField
                                    required={row.requiredLeft}
                                    disabled={
                                      index ===
                                      settingsInputFields.length - 3 ||
                                      !editable
                                    }
                                    placeholder={row.placeholderLeft}
                                    fieldName={row.fieldNameLeft}
                                    formik={formik}
                                  />
                                )}
                              </div>
                              {index === settingsInputFields.length - 3 && (
                                <Button
                                  color="primary"
                                  className="ml-3 mb-3"
                                  disabled={!formik.values[row.fieldNameLeft]}
                                  onClick={() =>
                                    handleCopyText(
                                      formik.values[row.fieldNameLeft],
                                      setIsCallBackurlCopy
                                    )
                                  }
                                >
                                  {isCallbackurlCopy ? "Copied" : "Copy"}
                                </Button>
                              )}
                            </div>
                          </Col>
                          <Col md={6}>
                            {row.fieldNameRight ===
                              "reservationTimeInterval" ? (
                              <>
                                <Label
                                  for="exampleSelect"
                                  className="inputfield"
                                >
                                  {row.placeholderRight}
                                </Label>
                                <section className="mb-3 input-group-alternative">
                                  <Select
                                    options={reservationTimeInterval}
                                    isDisabled={!editable}
                                    value={
                                      formik.values?.reservationTimeInterval
                                    }
                                    className="custom-select"
                                    isSearchable={false}
                                    styles={selectDropdownStyle}
                                    onChange={(selectedOption) => {
                                      formik.setFieldValue(
                                        "reservationTimeInterval",
                                        selectedOption
                                      );
                                    }}
                                  />
                                  {formik.touched?.reservationTimeInterval &&
                                    formik.errors.reservationTimeInterval && (
                                      <span className="error-message input-group-alternative">
                                        {formik.errors.reservationTimeInterval}
                                      </span>
                                    )}
                                </section>
                              </>
                            ) : (
                              <div className="flex align-items-end">
                                <div className="full-width">
                                  {row.fieldNameRight && (
                                    <InputField
                                      required={row.requiredRight}
                                      disabled={
                                        index ===
                                        settingsInputFields.length - 3 ||
                                        !editable
                                      }
                                      placeholder={row.placeholderRight}
                                      fieldName={row.fieldNameRight}
                                      formik={formik}
                                    />
                                  )}
                                </div>
                                {index === settingsInputFields.length - 3 && (
                                  <Button
                                    color="primary"
                                    className="ml-3 mb-3"
                                    disabled={
                                      !formik.values[row.fieldNameRight]
                                    }
                                    onClick={() =>
                                      handleCopyText(
                                        formik.values[row.fieldNameRight],
                                        setIsVerifyTokenCopy
                                      )
                                    }
                                  >
                                    {isVerifyTokenCopy ? "Copied" : "Copy"}
                                  </Button>
                                )}
                              </div>
                            )}
                          </Col>
                        </Row>
                      );
                    })}
                    <Label className="mb-2">Notification Settings</Label>
                    <Row >
                      <Col md={6}>
                        <Row className="title-div-rdt-picker">
                          <Col className="space-between-div">
                            <Label className="inputfield">Email Notification</Label>
                          </Col>
                          <Col className="center-div">
                            <label className="ml-3 custom-toggle">
                              <Input
                                type="checkbox"
                                disabled={editable ? false : true}
                                className="form-control"
                                name="isEmailNotificationEnabled"
                                checked={emailNotify}
                                onChange={(e) => setEmailNotify(e.target.checked)}
                              />
                              <span className="custom-toggle-slider rounded-circle" />
                            </label>
                          </Col>
                        </Row>
                        <Row className="title-div-rdt-picker">
                          <Col className="space-between-div">
                            <Label className="inputfield">Push Notification</Label>
                          </Col>
                          <Col className="center-div">
                            <label className="ml-3 custom-toggle">
                              <Input
                                type="checkbox"
                                className="form-control"
                                disabled={editable ? false : true}
                                name="isEmailNotificationEnabled"
                                checked={pushNotify}
                                onChange={(e) => setPushNotify(e.target.checked)}
                              />
                              <span className="custom-toggle-slider rounded-circle" />
                            </label>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                    <br></br>

                    <Label className="mb-2">Reservation Settings</Label>
                    <Row >
                      <Col md={6}>
                        <Row className="title-div-rdt-picker">
                          <Col className="space-between-div">
                            <Label className="inputfield">Reservation Through URL</Label>
                          </Col>
                          <Col className="center-div">
                            <label className="ml-3 custom-toggle">
                              <Input
                                type="checkbox"
                                disabled={editable ? false : true}
                                className="form-control"
                                name="reservationThroughUrl"
                                checked={isReservationUrl}
                                onChange={(e) => setIsReservationUrl(e.target.checked)}
                              />
                              <span className="custom-toggle-slider rounded-circle" />
                            </label>
                          </Col>
                        </Row>
                        {isReservationUrl ?

                          <Row className="title-div-rdt-picker">
                            <Input
                              placeholder="Enter Reservation URL"
                              fieldName="reservationUrl"
                              disabled={editable ? false : true}
                              type="text"
                              className="form-control"
                              style={{ width: "97%", margin: "12px 12px" }}
                              name="reservationUrl"
                              value={reservationUrl}
                              onChange={(e) => setReservationUrl(e.target.value)}
                            />
                          </Row>
                          : ""}
                      </Col>
                    </Row>
                    <br></br>
                    <Label className="mb-2">Service Hours</Label>
                    {days.map((day, index) => {
                      return (
                        <Row key={index}>
                          <Col md={5} xl={4}>
                            <Row className="title-div-rdt-picker">
                              <Col className="space-between-div">
                                <Label className="inputfield">{day}</Label>
                              </Col>
                              <Col className="center-div">
                                <Label className="inputfield">Opened</Label>
                                <label className="ml-3 custom-toggle">
                                  <Input
                                    type="checkbox"
                                    className="form-control"
                                    name="recommended"
                                    checked={serviceHours?.[index]}
                                    disabled={!editable}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setServiceHours({
                                          ...serviceHours,
                                          [index]: {},
                                        });
                                      } else
                                        setServiceHours((current) => {
                                          const state = { ...current };
                                          delete state[index];
                                          return state;
                                        });
                                    }}
                                  />
                                  <span className="custom-toggle-slider rounded-circle" />
                                </label>
                              </Col>
                            </Row>
                          </Col>
                          <Col md={7} xl={8}>
                            <Row>
                              <Col sm={6} xl={3}>
                                <InputGroup className="input-group-alternative mb-3 date-time-input-group">
                                  <InputGroupText>
                                    <FontAwesomeIcon icon={faClock} />
                                  </InputGroupText>
                                  <Datetime
                                    dateFormat={false}
                                    value={serviceHours?.[index]?.from1}
                                    renderInput={(props) => (
                                      <input
                                        {...props}
                                        value={
                                          serviceHours?.[index]?.from1
                                            ? props.value
                                            : ""
                                        }
                                      />
                                    )}
                                    onChange={(value) => {
                                      setServiceHours({
                                        ...serviceHours,
                                        [index]: {
                                          ...serviceHours[index],
                                          from1: value,
                                        },
                                      });
                                    }}
                                    inputProps={{
                                      disabled:
                                        !editable || !serviceHours?.[index],
                                    }}
                                  />
                                </InputGroup>
                              </Col>
                              <Col sm={6} xl={3}>
                                <InputGroup className="input-group-alternative mb-3 date-time-input-group">
                                  <InputGroupText>
                                    <FontAwesomeIcon icon={faClock} />
                                  </InputGroupText>
                                  <Datetime
                                    dateFormat={false}
                                    value={serviceHours?.[index]?.to1}
                                    renderInput={(props) => (
                                      <input
                                        {...props}
                                        value={
                                          serviceHours?.[index]?.to1
                                            ? props.value
                                            : ""
                                        }
                                      />
                                    )}
                                    onChange={(value) => {
                                      if (
                                        moment(
                                          serviceHours[index].from1
                                        ).isSame(moment(value))
                                      ) {
                                        setServiceHours({
                                          ...serviceHours,
                                          [index]: {
                                            ...serviceHours[index],
                                            to1: moment(
                                              serviceHours[index].from1
                                            ).add(1, "hour"),
                                          },
                                        });
                                      } else {
                                        setServiceHours({
                                          ...serviceHours,
                                          [index]: {
                                            ...serviceHours[index],
                                            to1: value,
                                          },
                                        });
                                      }
                                    }}
                                    inputProps={{
                                      disabled:
                                        !editable || !serviceHours?.[index],
                                    }}
                                  />
                                </InputGroup>
                              </Col>
                              <Col sm={6} xl={3}>
                                <InputGroup className="input-group-alternative mb-3 date-time-input-group">
                                  <InputGroupText>
                                    <FontAwesomeIcon icon={faClock} />
                                  </InputGroupText>
                                  <Datetime
                                    dateFormat={false}
                                    value={serviceHours?.[index]?.from2}
                                    renderInput={(props) => (
                                      <input
                                        {...props}
                                        value={
                                          serviceHours?.[index]?.from2
                                            ? props.value
                                            : ""
                                        }
                                      />
                                    )}
                                    onChange={(value) => {
                                      setServiceHours({
                                        ...serviceHours,
                                        [index]: {
                                          ...serviceHours[index],
                                          from2: value,
                                        },
                                      });
                                    }}
                                    inputProps={{
                                      disabled:
                                        !editable || !serviceHours?.[index],
                                    }}
                                  />
                                </InputGroup>
                              </Col>
                              <Col sm={6} xl={3}>
                                <InputGroup className="input-group-alternative mb-3 date-time-input-group">
                                  <InputGroupText>
                                    <FontAwesomeIcon icon={faClock} />
                                  </InputGroupText>
                                  <Datetime
                                    dateFormat={false}
                                    value={serviceHours?.[index]?.to2}
                                    renderInput={(props) => (
                                      <input
                                        {...props}
                                        value={
                                          serviceHours?.[index]?.to2
                                            ? props.value
                                            : ""
                                        }
                                      />
                                    )}
                                    onChange={(value) => {
                                      if (
                                        moment(
                                          serviceHours[index].from2
                                        ).isSame(moment(value))
                                      ) {
                                        setServiceHours({
                                          ...serviceHours,
                                          [index]: {
                                            ...serviceHours[index],
                                            to2: moment(
                                              serviceHours[index].from2
                                            ).add(1, "hour"),
                                          },
                                        });
                                      } else {
                                        setServiceHours({
                                          ...serviceHours,
                                          [index]: {
                                            ...serviceHours[index],
                                            to2: value,
                                          },
                                        });
                                      }
                                    }}
                                    inputProps={{
                                      disabled:
                                        !editable || !serviceHours?.[index],
                                    }}
                                  />
                                </InputGroup>
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      );
                    })}
                    {editable && formik.errors.serviceHours && (
                      <span className="error-message input-group-alternative">
                        {formik.errors.serviceHours}
                      </span>
                    )}
                    {updateSetting()}
                  </Form>
                </div>
              </TabPane>
              <TabPane tabId={1}>
                <div className="tab-content-inner-wrap">
                  <Form>
                    <Row>
                      <Col md={6} className="space-between-div mb-3">
                        <Label for="exampleSelect" className="inputfield">
                          Create Stripe Account
                        </Label>
                        <Button
                          color="outline-primary"
                          disabled={settings?.isStripeAccount}
                          type="button"
                          size="sm"
                          title={
                            settings?.isStripeAccount
                              ? "Already Account Created"
                              : "Create Account"
                          }
                          onClick={() => createStripeAccount()}
                        >
                          Create
                        </Button>
                      </Col>
                      <Col md={6} className="space-between-div mb-3">
                        <Label for="exampleSelect" className="inputfield">
                          To Add Bank Details
                        </Label>
                        <Button
                          color="outline-primary"
                          type="button"
                          size="sm"
                          onClick={() => linkBankAccounts()}
                        >
                          Add
                        </Button>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6} className="space-between-div mb-3">
                        <Label for="exampleSelect" className="inputfield">
                          View Your Accout Details
                        </Label>
                        <Button
                          color="outline-primary"
                          type="button"
                          size="sm"
                          onClick={() => stripeAccountLogin()}
                        >
                          View
                        </Button>
                      </Col>
                    </Row>
                    {updateSetting()}
                  </Form>
                </div>
              </TabPane>
              <TabPane tabId={2}>
                <div className="tab-content-inner-wrap">
                  <Form>
                    <Row>
                      <Col md={6} className="space-between-div mb-3">
                        <Label for="exampleSelect" className="inputfield">
                          Toggle Loyalty Program
                        </Label>
                        <label className="custom-toggle">
                          <Input
                            type="checkbox"
                            className="form-control"
                            name="recommended"
                            checked={allowLoyalty}
                            disabled={!editable}
                            onChange={() => {
                              setAllowLoyalty(!allowLoyalty);
                              if (allowLoyalty) {
                                formik.setFieldValue("rewardCoinsPrice", "");
                                formik.setFieldValue("rewardCoinsPerOrder", "");
                                formik.setFieldValue("minimumOrderPrice", "");
                                formik.setFieldValue(
                                  "minimumRewardedCoins",
                                  ""
                                );
                                formik.setFieldValue("maxRedeemableCoins", "");
                              }
                            }}
                          />
                          <span className="custom-toggle-slider rounded-circle" />
                        </label>
                      </Col>
                    </Row>
                    <Collapse isOpen={allowLoyalty}>
                      {allowLoyalty &&
                        loyaltyProgramInputFields.map((row, index) => {
                          return (
                            <Row key={index}>
                              <Col md={6}>
                                <InputField
                                  disabled={!editable}
                                  placeholder={row.placeholderLeft}
                                  fieldName={row.fieldNameLeft}
                                  formik={formik}
                                />
                              </Col>
                              {row?.fieldNameRight && (
                                <Col md={6}>
                                  <InputField
                                    disabled={!editable}
                                    placeholder={row.placeholderRight}
                                    fieldName={row.fieldNameRight}
                                    formik={formik}
                                  />
                                </Col>
                              )}
                            </Row>
                          );
                        })}
                    </Collapse>
                    {updateSetting()}
                  </Form>
                </div>
              </TabPane>
              <TabPane tabId={3}>
                <div className="tab-content-inner-wrap">
                  <Form>
                    {Array.isArray(formik.values.welcomePDF) &&
                      formik.values.welcomePDF.map((file, index) => (
                        <Row key={index}>
                          <Col md={6}>
                            <Label for="exampleSelect" className={`inputfield`}>
                              Welcome PDF {index + 1}
                            </Label>
                            <div className="space-between-div welcome-pdf-input cursor-pointer">
                              <InputGroup className="input-group-alternative mb-3">
                                <Input
                                  key={`welcomePDF-${index}`}
                                  className="custom-form-control mr-3"
                                  id="welcomePDF"
                                  type="file"
                                  disabled={!editable}
                                  accept="application/pdf"
                                  invalid={
                                    !!formik?.errors?.welcomePDF &&
                                    !!formik?.errors?.welcomePDF[index]?.fileUrl
                                  }
                                  onChange={(e) => {
                                    if (
                                      e.currentTarget.files &&
                                      e.currentTarget.files[0]
                                    ) {
                                      const updatedPdfs = [
                                        ...formik.values.welcomePDF,
                                      ];

                                      updatedPdfs[index] = {
                                        fileName:
                                          e.currentTarget.files[0].name.replace(
                                            ".pdf",
                                            ""
                                          ),
                                        fileUrl: e.currentTarget.files[0],
                                      };
                                      formik?.setFieldValue(
                                        "welcomePDF",
                                        updatedPdfs
                                      );
                                    }
                                  }}
                                />
                                <FormFeedback>
                                  {formik?.errors?.welcomePDF &&
                                    formik?.errors?.welcomePDF[index] &&
                                    formik?.errors?.welcomePDF[index]?.fileUrl}
                                </FormFeedback>
                              </InputGroup>
                            </div>
                          </Col>
                          <Col md={6}>
                            <Label for="exampleSelect" className={`inputfield`}>
                              Welcome PDF {index + 1} Name
                            </Label>
                            <div className="space-between-div cursor-pointer align-start">
                              <InputGroup className="mb-3">
                                <Input
                                  key={`${file.fileName}-${index}`}
                                  className="custom-form-control mr-3"
                                  value={file.fileName}
                                  disabled={!editable}
                                  autoFocus
                                  invalid={
                                    !!formik?.errors?.welcomePDF &&
                                    !!formik?.errors?.welcomePDF[index]
                                      ?.fileName
                                  }
                                  onChange={(e) => {
                                    const updatedPdfs = [
                                      ...formik.values.welcomePDF,
                                    ];

                                    updatedPdfs[index] = {
                                      ...updatedPdfs[index],
                                      fileName: e.target.value,
                                    };
                                    formik?.setFieldValue(
                                      "welcomePDF",
                                      updatedPdfs
                                    );
                                  }}
                                />
                                <FormFeedback>
                                  {formik?.errors?.welcomePDF &&
                                    formik?.errors?.welcomePDF[index] &&
                                    formik?.errors?.welcomePDF[index]?.fileName}
                                </FormFeedback>
                              </InputGroup>
                              {Array.isArray(formik.values.welcomePDF) &&
                                formik.values.welcomePDF.length - 1 ===
                                index && (
                                  <Button
                                    disabled={!editable}
                                    color="primary"
                                    type="button"
                                    onClick={() => handleAdd()}
                                  >
                                    <FontAwesomeIcon icon={faPlus} />
                                  </Button>
                                )}
                              <Button
                                color="primary"
                                disabled={!editable}
                                type="button"
                                onClick={() => handleRemove(index)}
                              >
                                <FontAwesomeIcon icon={faMinus} />
                              </Button>
                            </div>
                          </Col>
                        </Row>
                      ))}
                    <ul>
                      {Array.isArray(formik.values.welcomePDF) &&
                        formik.values.welcomePDF.map((file, index) => {
                          return (
                            typeof file?.fileUrl === "string" &&
                            ImageLinks.includes(
                              file?.fileUrl.split(":")[0]
                            ) && (
                              <li
                                key={index}
                                className={`${!editable && "disabled"} ${typeof file?.fileUrl === "string" &&
                                  "cursor-pointer"
                                  }`}
                                onClick={() =>
                                  typeof file.fileUrl === "string" &&
                                  window.open(file?.fileUrl, "_blank")
                                }
                              >
                                {file.fileName}
                              </li>
                            )
                          );
                        })}
                    </ul>
                    {formik.errors.welcomePDF &&
                      typeof formik.errors.welcomePDF === "string" && (
                        <span className="error-message input-group-alternative">
                          {formik.errors.welcomePDF}
                        </span>
                      )}
                    {updateSetting()}
                  </Form>
                </div>
              </TabPane>
              <TabPane tabId={4}>
                <div className="tab-content-inner-wrap">
                  <Form>
                    <h4>AI Tone & Personality</h4>
                    <Row>
                      <Col md={6}>
                        <Label className="inputfield">Tone</Label>
                        <Input
                          id="tone"
                          className="form-select"
                          style={{ width: "100%", padding: "0.5rem", borderRadius: "0.375rem", marginRight: "0.5rem" }}
                          name="tone"
                          type="select"
                          onChange={(e) => {
                            formik.setFieldValue("tone", e.target.value);
                          }}
                          value={formik.values.tone || ""}
                        >
                          <option value="" selected>Select Tone</option>
                          <option value="link">Casial</option>
                          <option value="pdf">Proffesional</option>
                        </Input>
                      </Col>
                      <Col md={6}>
                        <Label className="inputfield">Proffesionality Level</Label>
                        <Input
                          id="proffesionality_level"
                          className="form-select"
                          style={{ width: "100%", padding: "0.5rem", borderRadius: "0.375rem", marginRight: "0.5rem" }}
                          name="proffesionality_level"
                          type="select"
                          onChange={(e) => {
                            formik.setFieldValue("proffesionality_level", e.target.value);
                          }}
                          value={formik.values.proffesionality_level || ""}
                        >
                          <option value="" selected>Select Tone</option>
                          <option value="link">Casial</option>
                          <option value="pdf">Proffesional</option>
                        </Input>
                      </Col>
                    </Row>
                    <h4>Business-Specific Pheases</h4>
                    <Row>
                      <Col md={6}>
                        <Label className="inputfield">Tagline or Slogan</Label>
                        <Input
                          type="text"
                          name="tagline"
                          className="custom-form-control mb-3"
                          value={formik.values.tagline}
                          onChange={formik.handleChange}
                        />
                      </Col>
                      <Col md={6}>
                        <Label className="inputfield">Barnd Terms</Label>
                        <Input
                          type="text"
                          name="googleAccountId"
                          className="custom-form-control mb-3"
                          value={formik.values.googleAccountId}
                          onChange={formik.handleChange}
                        />
                      </Col>
                    </Row>
                    <h4>Trigger-Based Auto Replies</h4>
                    <Row>
                      {triggerReplies.map((item, index) => (
                        <Col md={12} key={index}>
                          <Row className="align-items-center mb-3">
                            {/* Title */}
                            <Col md={4} sm={12}>
                              <InputGroup className="input-group-alternative">
                                <Input
                                  type="text"
                                  name={`triggerReplies[${index}].title`}
                                  placeholder="Title"
                                  value={item.title}
                                  onChange={formik.handleChange}
                                  className="custom-form-control"
                                />
                              </InputGroup>
                            </Col>

                            {/* Message */}
                            <Col md={6} sm={12}>
                              <InputGroup className="input-group-alternative">
                                <Input
                                  type="text"
                                  name={`triggerReplies[${index}].message`}
                                  placeholder="Message"
                                  value={item.message}
                                  onChange={formik.handleChange}
                                  className="custom-form-control"
                                />
                              </InputGroup>
                            </Col>

                            {/* Buttons */}
                            <Col md={2} sm={12} className="d-flex gap-2">
                              {triggerReplies.length - 1 === index && (
                                <Button
                                  color="primary"
                                  type="button"
                                  onClick={() => handleAddTriggerReply('pdf')}
                                >
                                  <FontAwesomeIcon icon={faPlus} />
                                </Button>
                              )}
                              <Button
                                color="danger"
                                type="button"
                                onClick={() => handleRemoveTriggerReply(index, 'pdf')}
                              >
                                <FontAwesomeIcon icon={faMinus} />
                              </Button>
                            </Col>
                          </Row>
                        </Col>
                      ))}
                    </Row>

                    {/* Fallback Behavior Section */}
                    <h4>Fallback Behavior</h4>
                    <Row className="mb-3">
                      <Col md={12}>
                        <div className="d-flex flex-column gap-2">
                          <Label check>
                            <Input
                              type="radio"
                              name="fallbackBehavior"
                              className="form-check-input"
                              value="defaultMessage"
                              checked={formik.values.fallbackBehavior === "defaultMessage"}
                              onChange={formik.handleChange}
                            />
                            Show default message
                          </Label>
                          <Label check>
                            <Input
                              type="radio"
                              name="fallbackBehavior"
                              value="routeToHuman"
                              checked={formik.values.fallbackBehavior === "routeToHuman"}
                              onChange={formik.handleChange}
                            />
                            Route to human staff
                          </Label>
                          <Label check>
                            <Input
                              type="radio"
                              name="fallbackBehavior"
                              value="logUnanswered"
                              checked={formik.values.fallbackBehavior === "logUnanswered"}
                              onChange={formik.handleChange}
                            />
                            Log unanswered for review
                          </Label>
                        </div>
                      </Col>
                    </Row>

                    {/* Response Settings Section */}
                    <h4>Response Settings</h4>
                    <Row>
                      <Col md={6}>
                        <Label className="inputfield" for="responseStyle">Response Style</Label>
                        <Input
                          id="responseStyle"
                          className="form-select"
                          style={{ width: "100%", padding: "0.5rem", borderRadius: "0.375rem", marginRight: "0.5rem" }}
                          name="responseStyle"
                          type="select"
                          onChange={(e) => {
                            formik.setFieldValue("responseStyle", e.target.value);
                          }}
                          value={formik.values.tone || ""}
                        >
                         <option value="balanced">Balanced</option>
                          <option value="friendly">Friendly</option>
                          <option value="formal">Formal</option>
                        </Input>
                      </Col>
                      <Col md={6}>
                        <Label className="inputfield" for="messagePacing">Message Pacing</Label>
                        <Input
                          id="messagePacing"
                          className="form-select"
                          style={{ width: "100%", padding: "0.5rem", borderRadius: "0.375rem", marginRight: "0.5rem" }}
                          name="messagePacing"
                          type="select"
                          onChange={(e) => {
                            formik.setFieldValue("messagePacing", e.target.value);
                          }}
                          value={formik.values.messagePacing || ""}
                        >
                         {/* {<option value="instant">Instant</option>} */}
                          <option value="natural">Natural (human-like)</option>
                          <option value="delayed">Delayed</option>
                        </Input>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={12}>
                        <Label className="inputfield">Instruction</Label>
                        <Input
                          type="textarea"
                          name="instruction"
                          disabled={true}
                          rows="6"
                          className="custom-form-control mb-3"
                          value={formik.values.instruction}
                          onChange={formik.handleChange}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Label className="inputfield">Secret Key</Label>
                        <Input
                          type="text"
                          name="secretKey"
                          disabled={true}
                          className="custom-form-control mb-3"
                          value={formik.values.secretKey}
                          onChange={formik.handleChange}
                        />
                      </Col>
                      <Col md={6}>
                        <Label className="inputfield">Temperature</Label>
                        <Input
                          type="number"
                          name="temperature"
                          className="custom-form-control mb-3"
                          value={formik.values.temperature}
                          onChange={formik.handleChange}
                          min="0.1"
                          max="2.0"
                          step="0.1"
                        />
                      </Col>
                      <Col md={6}>
                        <Label className="inputfield">API Key</Label>
                        <Input
                          type="text"
                          name="apiKey"
                          className="custom-form-control mb-3"
                          value={formik.values.apiKey}
                          onChange={formik.handleChange}
                        />
                      </Col>
                    </Row>
                    <h4>Google Client Settings</h4>
                    <Row>
                      <Col md={6}>
                        <Label className="inputfield">Google Client ID</Label>
                        <Input
                          type="text"
                          name="googleClientId"
                          className="custom-form-control mb-3"
                          value={formik.values.googleClientId}
                          onChange={formik.handleChange}
                        />
                      </Col>
                      <Col md={6}>
                        <Label className="inputfield">Google Refresh Token</Label>
                        <Input
                          type="text"
                          name="googleRefreshToken"
                          className="custom-form-control mb-3"
                          value={formik.values.googleRefreshToken}
                          onChange={formik.handleChange}
                        />
                      </Col>
                      <Col md={6}>
                        <Label className="inputfield">Google Location ID</Label>
                        <Input
                          type="text"
                          name="googleLocationId"
                          className="custom-form-control mb-3"
                          value={formik.values.googleLocationId}
                          onChange={formik.handleChange}
                        />
                      </Col>
                      <Col md={6}>
                        <Label className="inputfield">Google Account ID</Label>
                        <Input
                          type="text"
                          name="googleAccountId"
                          className="custom-form-control mb-3"
                          value={formik.values.googleAccountId}
                          onChange={formik.handleChange}
                        />
                      </Col>
                      <Col md={12}>
                        <Label className="inputfield">Google Client Secret</Label>
                        <Input
                          type="text"
                          name="googleClientSecret"
                          className="custom-form-control mb-3"
                          value={formik.values.googleClientSecret}
                          onChange={formik.handleChange}
                        />
                      </Col>
                    </Row>

                    {updateAISetting()}
                  </Form>
                </div>
              </TabPane>

            </TabContent>
          </div>
        </CardBody>
      </Card>
    </div >
  );
}
